import { Button, Form, Input, InputNumber, Table, Modal } from 'antd';
import type { ColumnType, TableProps } from 'antd/lib/table';
import { useStoreActions, useStoreState } from '../../store';
import { useEffect, useState } from 'react';
import type { TntJob, TntJobV2 } from '../../store/services/TimeInTransitService/types';
import { ShipiumTag } from '../shared/ShipiumTag';
import {
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  CheckCircleOutlined,
  CloudDownloadOutlined,
} from '@ant-design/icons';
import { hashTNTSearch } from '../../store/timeInTransit';
import { jobFailureMessage } from './tntUploadError';
import axios from 'axios';
import getGatewayUri from '../../store/services/getGatewayUri';
import { useMutation } from '@tanstack/react-query';
import { partnerService } from '~/services';

import { findDataSetId } from './findDataSetId';
import type { DataSetUsage } from './findDataSetId';

export interface TntProdTableProps {
  data: TntJobV2[];
  loading?: boolean;
  tableProps?: TableProps<TntJobV2>;
  removeColumns: string[];
  rowKeyOverride?: keyof TntJobV2;
  /**
   * tnt_management source whose dataset the Snapshot download targets. This table is shared by
   * every tab, and the dataSetId must come from the same source as the row's dataSetVersionId —
   * a mismatched pair is a request the backend cannot satisfy.
   */
  tntSourceName?: string;
  /** Usage of the dataset to resolve. */
  dataSetUsage?: DataSetUsage;
}

interface EditableColumnType<T> extends ColumnType<T> {
  editable?: boolean;
}

interface EditableCellProps extends React.HTMLAttributes<HTMLElement> {
  editing: boolean;
  dataIndex: string;
  title: any;
  inputType: 'number' | 'text';
  record: TntJobV2;
  index: number;
}

const EditableCell = ({
  editing,
  dataIndex,
  title,
  inputType,
  record,
  index,
  children,
  ...restProps
}: EditableCellProps) => {
  const inputNode = inputType === 'number' ? <InputNumber /> : <Input />;

  return (
    <td {...restProps}>
      {editing ? (
        <Form.Item
          name={dataIndex}
          style={{ margin: 0 }}
          rules={[
            {
              required: true,
              message: `${title} is required.`,
            },
          ]}
        >
          {inputNode}
        </Form.Item>
      ) : (
        children
      )}
    </td>
  );
};

export const jobUploadStatusToTag: Record<TntJob['uploadJobStatus'], React.ReactNode> = {
  IN_PROGRESS: (
    <ShipiumTag dark={true} color={'Blue'}>
      <ClockCircleOutlined style={{ paddingRight: '5px' }} />
      In Progress
    </ShipiumTag>
  ),
  COMPLETE: (
    <ShipiumTag dark={true} color={'Teal'}>
      <CheckCircleOutlined style={{ paddingRight: '5px' }} />
      Processed
    </ShipiumTag>
  ),
  FAILED: (
    <ShipiumTag color="Coral" dark={true}>
      <ExclamationCircleOutlined style={{ paddingRight: '5px' }} />
      Failed
    </ShipiumTag>
  ),
};

export const tntJobStatusPriority: TntJob['uploadJobStatus'][] = ['IN_PROGRESS', 'COMPLETE', 'FAILED'];

function tntJobStatusSort(a: TntJobV2, b: TntJobV2) {
  const aStatus = a.uploadJobStatus || 'COMPLETE';
  const bStatus = b.uploadJobStatus || 'COMPLETE';

  const aPriority = tntJobStatusPriority.indexOf(aStatus) + 1;
  const bPriority = tntJobStatusPriority.indexOf(bStatus) + 1;

  return aPriority - bPriority;
}

// Jobs that fail before the parse stage (e.g. the source file could not be read) never produce
// an error file. Offering the download for those rows guarantees a 404 from the gateway, so the
// button is hidden instead. Mirrors the guard in downloadTntErrorFileHandler.
export const hasErrorFile = (row: Pick<TntJobV2, 'errorInfo'>): boolean =>
  Boolean(row?.errorInfo?.errorFileLocation?.fileBucket && row?.errorInfo?.errorFileLocation?.filePath);

export const TntTable = ({
  data,
  loading,
  tableProps,
  removeColumns,
  rowKeyOverride,
  tntSourceName = 'partner-provided-postal',
  dataSetUsage = 'PROD',
}: TntProdTableProps) => {
  const [form] = Form.useForm();
  const [updatedData, setUpdatedData] = useState(data);
  const [modal, contextHolder] = Modal.useModal();
  const downloadTntRecordsBySearch = useStoreActions((actions) => actions.timeInTransit.downloadTntRecordsBySearchV2);
  const timeInTransitState = useStoreState((state) => state.timeInTransit.timeInTransitState);
  const downloadingTnt = useStoreState((state) => state.timeInTransit.downloadingTnt);
  const [users, setUsers] = useState(new Map());

  const getUserMutation = useMutation({
    mutationFn: async (userIds: string[]) => {
      const results = await Promise.allSettled(userIds.map((userId) => partnerService.getPartnerUser(userId)));
      return results.reduce((acc, user, index) => {
        acc.set(userIds[index], user);
        return acc;
      }, new Map());
    },
    onSuccess: (data) => {
      setUsers(data);
    },
  });

  const getTableUsers = async () => {
    const uniqueUserIds = [...new Set(data.map((record) => record.createdBy))];
    if (uniqueUserIds.length > 0) {
      getUserMutation.mutate(uniqueUserIds);
    }
  };

  // `await` keeps a rejected request inside the try/catch, so a failure surfaces as the modal
  // below rather than an unhandled rejection.
  const downloadErrorFile = async (row: TntJobV2) => {
    try {
      const val = await axios({
        method: 'POST',
        url: `${getGatewayUri()}/downloadTntErrorFile`,
        data: {
          tntJobId: row.uploadJobId,
        },
        withCredentials: true,
      });
      const link = document.createElement('a');
      link.setAttribute('href', val.data.downloadUrl);
      link.setAttribute('download', `${row.tntDataSetName}-${row.tntDataSetVersionId}-Error`);
      document.body.appendChild(link);
      link.click();
    } catch (error) {
      modal.error({
        title: 'Failed',
        content: 'Failed to download error file.',
      });
    }
  };

  useEffect(() => {
    setUpdatedData(data);
    getTableUsers();
  }, [data]);

  const [editingKey, setEditingKey] = useState<string | number>('');

  const isEditing = (record: TntJobV2) => record.uploadJobId === editingKey;

  const cancel = () => {
    setEditingKey('');
  };
  const columns: EditableColumnType<TntJobV2>[] = [
    {
      title: 'Name',
      dataIndex: 'tntDataSetName',
      editable: true,
      sorter: (a: TntJobV2, b: TntJobV2) => {
        return (a.tntDataSetName ?? '').localeCompare(b.tntDataSetName ?? '');
      },
    },
    {
      title: 'Description',
      dataIndex: 'tntDataSetDescription',
      editable: true,
      sorter: (a: TntJobV2, b: TntJobV2) => {
        return (a.tntDataSetDescription ?? '').localeCompare(b.tntDataSetDescription ?? '');
      },
    },

    {
      title: 'Version',
      dataIndex: 'versionNumber',
      editable: true,
      sorter: (a: TntJobV2, b: TntJobV2) => {
        return (a.tntDataSetVersionId ?? '').localeCompare(b.tntDataSetVersionId ?? '');
      },
    },

    {
      title: 'Created by',
      sorter: (a: TntJobV2, b: TntJobV2) => {
        const userA = users.get(a.createdBy)?.givenName + users.get(a.createdBy)?.surname;
        const userB = users.get(b.createdBy)?.givenName + users.get(b.createdBy)?.surname;
        return userA.localeCompare(userB);
      },
      render: (row: TntJobV2) => {
        const user = users.get(row.createdBy)?.value;
        return user ? `${user.givenName} ${user.surname}` : 'Unknown';
      },
    },

    {
      title: 'Created',
      defaultSortOrder: 'descend',
      sorter: (a: TntJobV2, b: TntJobV2) => new Date(a.createdAt ?? 0).valueOf() - new Date(b.createdAt ?? 0).valueOf(),
      render: (row: TntJobV2) => {
        const timestamp = row.createdAt;
        return (
          <>
            {timestamp
              ? new Date(timestamp).toLocaleDateString('sv-SE') +
                ' ' +
                new Date(timestamp).toLocaleString('en-US').split(', ')[1]
              : ''}
          </>
        );
      },
    },

    {
      title: 'Status',
      width: '150px',
      sorter: tntJobStatusSort,
      render: (row: TntJobV2) => {
        const status = row.uploadJobStatus || 'COMPLETE';
        return (
          <div
            style={{ display: 'flex', justifyContent: 'center' }}
            onClick={() => {
              row.uploadJobStatus === 'FAILED' &&
                modal.error({
                  title: 'Upload failed',
                  content: jobFailureMessage(row),
                });
            }}
          >
            {jobUploadStatusToTag[status]}
          </div>
        );
      },
    },
    {
      title: 'Actions',
      width: '150px',
      render: (row: TntJobV2) => {
        const tntSearch = {
          dataSetId: findDataSetId(timeInTransitState, tntSourceName, dataSetUsage) || '',
          dataSetVersionId: row.tntDataSetVersionId,
        };
        // trim and replace whitespace

        const filename = (row.tntDataSetName ?? '').trim().replace(/ +/g, '-');
        const hash = hashTNTSearch(tntSearch);

        return (
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '5px' }}>
            <Button
              title="Download Snapshot"
              loading={downloadingTnt[hash]}
              onClick={() => downloadTntRecordsBySearch({ name: filename, tntSearch })}
            >
              Snapshot <CloudDownloadOutlined />
            </Button>
            {hasErrorFile(row) && (
              <Button
                title="Download Error"
                //loading={downloadingTnt[hash]}
                onClick={() => downloadErrorFile(row)}
              >
                Error <CloudDownloadOutlined />
              </Button>
            )}
          </div>
        );
      },
    },
  ];

  const filteredColumns: any = columns
    .filter((col) => !removeColumns.includes(col.title?.toString().toLowerCase() || ''))
    .map((col) => {
      if (!col.editable) {
        return col;
      }
      return {
        ...col,
        onCell: (record: TntJobV2) => ({
          record,
          inputType: 'text',
          dataIndex: col?.dataIndex || '',
          title: col?.title || '',
          editing: isEditing(record),
        }),
      };
    });

  return (
    <Form form={form} component={false}>
      <Table
        pagination={{
          defaultPageSize: 20,
          showSizeChanger: true,
          size: 'small',
          onChange: cancel,
        }}
        components={{
          body: {
            cell: EditableCell,
          },
        }}
        size="small"
        loading={loading}
        columns={filteredColumns}
        dataSource={updatedData}
        rowKey={(record) => String(rowKeyOverride ? record[rowKeyOverride] : record.uploadJobId)}
        {...tableProps}
      />
      {contextHolder}
    </Form>
  );
};
